import { generateRealmLocationOptionValue } from '../../../../../../shared/frontend/scripts/global-search/autocomplete/utils';
import { SearchFiltersType } from '../../../../../../shared/frontend/scripts/global-search/types';
import { buildUrl } from '../../../../../../shared/frontend/libs/utils';
import { TCharacterRole } from '../../../../../../shared/common/types/common';
import { getGameConfig } from '../../../../../../shared/gameConfig';
import { namedRoutes } from '../../bundles/routes';
import { TRegionSummary } from '../../libs/store';

interface BaseSearchParams {
  region?: TRegionSummary;
  realm?: { slug: string };
  limit?: number;
  includeLogo?: boolean;
}

interface CharacterSearchParams extends BaseSearchParams {
  specId?: number;
  classId?: number;
}

type SearchType = 'character' | 'guild';

interface MetricsConfig {
  type: SearchType;
  sortField: string;
  filterField: string;
}

const generateBaseTopMetricsSearchParams = ({
  region,
  realm,
  limit,
  type,
  sortField,
  filterField
}: BaseSearchParams & MetricsConfig) => {
  const result: Record<string, any> = {
    type,
    offset: 0,
    limit: limit || 10,
    [`sort[${sortField}]`]: 'desc',
    [`${filterField}[0][gte]`]: 1
  };

  if (region?.slug !== 'world') {
    if (realm && region) {
      result['realm[0][eq]'] = generateRealmLocationOptionValue({
        region,
        realm
      });
    } else if (region) {
      result['region[0][eq]'] = region.slug;
    }
  }

  return result;
};

const addCharacterSpecificParams = (params: CharacterSearchParams) => {
  const result: Record<string, any> = {};

  if (params.specId) {
    result['specId[0][eq]'] = params.specId;
  }

  if (params.classId) {
    result['classId[0][eq]'] = params.classId;
  }

  // filter to current level cap in order to omit pre-squish people
  const gameConfig = getGameConfig();
  result['level[0][gte]'] = gameConfig.CurrentLevelCap;

  return result;
};

export const generateTopIlvlCharactersParams = (params: CharacterSearchParams) => {
  return {
    ...generateBaseTopMetricsSearchParams({
      ...params,
      type: 'character',
      sortField: 'itemLevelEquipped',
      filterField: 'itemLevelEquipped'
    }),
    ...addCharacterSpecificParams(params)
  };
};

export const generateTopIlvlGuildsParams = (params: BaseSearchParams) => {
  return {
    ...generateBaseTopMetricsSearchParams({
      ...params,
      type: 'guild',
      sortField: 'itemLevelEquipped.all.top20',
      filterField: 'itemLevelEquipped.all.top20'
    }),
    ...(params.includeLogo ? { includeLogo: true } : {})
  };
};

export const generateTopAchievementsCharactersParams = (params: CharacterSearchParams) => {
  return {
    ...generateBaseTopMetricsSearchParams({
      ...params,
      type: 'character',
      sortField: 'achievementPoints',
      filterField: 'achievementPoints'
    }),
    ...addCharacterSpecificParams(params)
  };
};

interface IGenerateGlobalSearchMythicPlusRunsPathParams {
  season: string;
  loadout?: { text: string; filterPath?: 'member' | 'video' };
  region?: string;
  dungeonZoneId?: number;
  minMythicLevel?: number;
  maxMythicLevel?: number;
  mythicLevel?: number;
  seasonWeekStart?: number;
  seasonWeekEnd?: number;
  timedState?: 'all' | 'timed' | 'depleted';
  memberSpecIds?: number[];
  routeFamilyId?: string;
  requireVideo?: boolean;
  videoRole?: TCharacterRole;
  videoSpecId?: number;
  sort?: 'mythicLevel' | 'completedAt';
}

export function generateGlobalSearchMythicPlusRunsPath(params: IGenerateGlobalSearchMythicPlusRunsPathParams): string {
  const {
    season,
    loadout,
    region,
    dungeonZoneId,
    minMythicLevel,
    maxMythicLevel,
    mythicLevel,
    seasonWeekStart,
    seasonWeekEnd,
    timedState,
    memberSpecIds,
    routeFamilyId,
    requireVideo,
    videoRole,
    videoSpecId,
    sort = 'mythicLevel'
  } = params;

  const queryParams: Record<string, unknown> = {
    type: SearchFiltersType.MythicPlusRuns,
    season: [{ eq: season }]
  };

  if (region && region !== 'world') {
    queryParams.region = [{ eq: region }];
  }

  if (dungeonZoneId != null) {
    queryParams.dungeonZoneId = [{ eq: dungeonZoneId }];
  }

  const mythicLevelFilters: Record<string, number>[] = [];
  if (minMythicLevel != null && minMythicLevel !== mythicLevel) {
    mythicLevelFilters.push({ gte: minMythicLevel });
  }
  if (maxMythicLevel != null && maxMythicLevel !== mythicLevel) {
    mythicLevelFilters.push({ lte: maxMythicLevel });
  }
  if (mythicLevel != null) {
    mythicLevelFilters.push({ eq: mythicLevel });
  }
  if (mythicLevelFilters.length > 0) {
    queryParams.mythicLevel = mythicLevelFilters;
  }

  const seasonWeekFilters: Record<string, number>[] = [];
  if (seasonWeekStart != null) {
    seasonWeekFilters.push({ gte: seasonWeekStart });
  }
  if (seasonWeekEnd != null) {
    seasonWeekFilters.push({ lte: seasonWeekEnd });
  }
  if (seasonWeekFilters.length > 0) {
    queryParams.seasonWeek = seasonWeekFilters;
  }

  if (timedState === 'timed') {
    queryParams.numChests = [{ gte: 1 }];
  } else if (timedState === 'depleted') {
    queryParams.numChests = [{ eq: 0 }];
  }

  if (memberSpecIds?.length) {
    queryParams.memberSpecIds = memberSpecIds.map((specId) => ({ eq: specId }));
  }

  if (routeFamilyId) {
    queryParams.routeFamilyId = [{ eq: routeFamilyId }];
  }

  if (loadout) {
    const { text, filterPath = 'member' } = loadout;
    const filterPathKey = filterPath === 'member' ? 'memberTalentLoadout' : 'videoTalentLoadout';
    queryParams[filterPathKey] = [{ contains: text }];
  }

  if (requireVideo) {
    queryParams.videoCount = [{ gte: 1 }];
  }
  if (videoRole != null) {
    queryParams.videoRole = [{ eq: videoRole }];
  }
  if (videoSpecId != null) {
    queryParams.videoSpecIds = [{ eq: videoSpecId }];
  }

  queryParams.sort = { [sort]: 'desc' };

  return buildUrl(namedRoutes.Search, queryParams);
}

interface IGenerateGlobalSearchRaidEncountersPathParams {
  raid: string;
  difficulty: string;
  loadout?: { text: string; filterPath?: 'member' | 'video' };
  region?: string;
  raidBossId?: number;
  periodStart?: number;
  periodEnd?: number;
  // Kill-time range in milliseconds, half-open `[min, maxExclusive)`. NOTE: the raid_encounters
  // `durationMs` filter takes its INPUT in seconds (the backend multiplies by 1000 — see
  // parseNumericFilterValue in elasticSearchServiceFactory), so these ms bounds are converted to
  // seconds when emitted. durationMs only supports gte/lte and stored durations are integer ms, so the
  // exclusive upper `< maxExclusive` is emitted as `lte: (maxExclusive - 1) / 1000` (exact for integers).
  durationMsMin?: number;
  durationMsMaxExclusive?: number;
}

export function generateGlobalSearchRaidEncountersPath(params: IGenerateGlobalSearchRaidEncountersPathParams): string {
  const { raid, difficulty, loadout, region, raidBossId, periodStart, periodEnd, durationMsMin, durationMsMaxExclusive } =
    params;

  const queryParams: Record<string, unknown> = {
    type: SearchFiltersType.RaidEncounters,
    raid: [{ eq: raid }],
    difficulty: [{ eq: difficulty }]
  };

  if (region && region !== 'world') {
    queryParams.region = [{ eq: region }];
  }

  if (raidBossId != null) {
    queryParams.raidBossId = [{ eq: String(raidBossId) }];
  }

  const periodFilters: Record<string, number>[] = [];
  if (periodStart != null) {
    periodFilters.push({ gte: periodStart });
  }
  if (periodEnd != null) {
    periodFilters.push({ lte: periodEnd });
  }
  if (periodFilters.length > 0) {
    queryParams.period = periodFilters;
  }

  // durationMs filter input is in SECONDS (backend converts to ms via round(seconds * 1000)).
  const durationFilters: Record<string, number>[] = [];
  if (durationMsMin != null && durationMsMin > 0) {
    durationFilters.push({ gte: durationMsMin / 1000 });
  }
  if (durationMsMaxExclusive != null) {
    durationFilters.push({ lte: (durationMsMaxExclusive - 1) / 1000 });
  }
  if (durationFilters.length > 0) {
    queryParams.durationMs = durationFilters;
  }

  if (loadout) {
    const { text, filterPath = 'member' } = loadout;
    const filterPathKey = filterPath === 'member' ? 'memberTalentLoadout' : 'videoTalentLoadout';
    queryParams[filterPathKey] = [{ contains: text }];
  }

  queryParams.sort = { pullEndedAt: 'desc' };

  return buildUrl(namedRoutes.Search, queryParams);
}
